import { useIsMobileHook } from '@teliaee/telia-common-components';
import React, { useContext } from 'react';
import { CardContent, Grid, GridColumn, Icon, Selector, Text, TSelectorValue, TSelectSimpleValue } from 'telia-front-react';
import { TranslationsContext } from '@teliaee/telia-translate';
import { twebTranslateOrEmpty } from '../util/TranslateUtil';
import { Prices } from './MeluCalculator';
import {
  callsSelectorProps,
  disableAllPips,
  disablePipsLargerThan,
  disableSmallestPip,
  enableAllPips,
  getDataPrices,
  internetDataSelectorProps,
  internetSelectorProps,
  onlyOneDeviceEnabledPips,
  resolveInternetDataIndexFromKey,
  resolveInternetDataKeyFromIndex,
  resolveInternetDataValueFromIndex
} from './MeluDataUtil';
import { observer } from 'mobx-react';

interface Props {
  internetDeviceCount: number;
  callsDeviceCount: number;
  internetDataAmount: number;
  setInternetDeviceCount: (value: number) => void;
  setCallsDeviceCount: (value: number) => void;
  setInternetDataAmount: (value: number) => void;
  setRoamingDataAmount: (value: string) => void;
  prices?: Prices;
}

const MeluChooseParametersCard = observer((props: Props) => {
  const {
    internetDeviceCount,
    callsDeviceCount,
    internetDataAmount,
    setInternetDeviceCount,
    setCallsDeviceCount,
    setInternetDataAmount,
    setRoamingDataAmount,
    prices
  } = props;
  const { translate, translateOrEmpty } = useContext(TranslationsContext);
  const { isMobileOrTablet } = useIsMobileHook();
  const [disabledCallsPips, setDisabledCallsPips] = React.useState(disablePipsLargerThan(internetDeviceCount));
  const [disabledDataPips, setDisabledDataPips] = React.useState(enableAllPips());
  const dataPrices = getDataPrices(prices);

  const updateInternetDeviceCount = (value: TSelectorValue) => {
    const count = +value;
    setInternetDeviceCount(count);

    if (count === 0) {
      setDisabledCallsPips(onlyOneDeviceEnabledPips());
      setDisabledDataPips(disableAllPips());
      setCallsDeviceCount(1);
    } else {
      setDisabledCallsPips(disablePipsLargerThan(count));
      if (count < callsDeviceCount) {
        setCallsDeviceCount(count);
      }
      setDisabledDataPips(count > 1 ? disableSmallestPip() : enableAllPips());
      if (internetDataAmount < 5) {
        setInternetDataAmount(resolveInternetDataKeyFromIndex(1, dataPrices));
        setRoamingDataAmount(resolveInternetDataValueFromIndex(1, dataPrices).roaming);
      }
      updateRoamingDataAmount(null, undefined, count);
    }
  };

  const updateCallsDeviceCount = (value: TSelectSimpleValue) => setCallsDeviceCount(+value);

  const updateInternetDataAmount = (value: TSelectSimpleValue) => {
    const newInternetDataAmountValue = resolveInternetDataKeyFromIndex(+value, dataPrices);
    setInternetDataAmount(newInternetDataAmountValue);
    updateRoamingDataAmount(+value, newInternetDataAmountValue);
  };

  const updateRoamingDataAmount = (newValue?: number, newInternetDataAmount: number = internetDataAmount, deviceCount: number = internetDeviceCount) => {
    const value = newValue || resolveInternetDataIndexFromKey(newInternetDataAmount, dataPrices);
    const roamingData =
      deviceCount === 1 && dataPrices[`${newInternetDataAmount}`]?.singleDevice
        ? resolveInternetDataValueFromIndex(value, dataPrices).singleDevice.roaming
        : resolveInternetDataValueFromIndex(value, dataPrices).roaming;
    setRoamingDataAmount(roamingData);
  };

  const specialOffersLabel = twebTranslateOrEmpty(translateOrEmpty, 'meluCalculator.chooseParametersCard.specialOffersLabel');

  return (
    <GridColumn width={['lg-6']}>
      <CardContent padding={!isMobileOrTablet && { left: 'none', right: 'medium' }}>
        <Text>
          <p className="text-center text-left-sm">
            <strong>{translate('meluCalculator.chooseParametersCard.internetDeviceCountTitle')}</strong>
          </p>
          <Selector {...internetSelectorProps(translate)} itemWidth={['sm-2']} onChange={updateInternetDeviceCount} value={`${internetDeviceCount}`} />
          <p className="text-center text-left-sm">
            <strong>{translate('meluCalculator.chooseParametersCard.callsDeviceCountTitle')}</strong>
          </p>
          <Selector
            {...callsSelectorProps(disabledCallsPips, translate)}
            itemWidth={['sm-2']}
            onChange={updateCallsDeviceCount}
            value={`${callsDeviceCount}`}
          />
          <p className="text-center text-left-sm">
            <strong>{translate('meluCalculator.chooseParametersCard.internetDataAmountTitle')}</strong>
          </p>
          <Selector
            {...internetDataSelectorProps(disabledDataPips, translate, dataPrices)}
            value={`${resolveInternetDataIndexFromKey(internetDataAmount, dataPrices)}`}
            onChange={updateInternetDataAmount}
          />
          {specialOffersLabel && (
            <Grid gutter="none">
              <GridColumn>
                <Grid noWrap gutter="small">
                  <GridColumn width={['min']}>
                    <Icon modifiers="#star" align="none" />
                  </GridColumn>
                  <GridColumn width={['max']}>
                    <p className="text-small">{specialOffersLabel}</p>
                  </GridColumn>
                </Grid>
              </GridColumn>
            </Grid>
          )}
        </Text>
      </CardContent>
    </GridColumn>
  );
});

export default MeluChooseParametersCard;
